import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  FileVersionData,
  SourceFileVersionData,
  FileDependencyItem,
} from "@ServerTypes/fileOperations/fileOperations.schemas";
import type { ApiPostResponse } from "@Services/axiosShared";
import type { UpdateFileUnitsRequest } from "@ServerTypes/queries/queries.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";

/** File version list item (a version entry for a file) */
export type FileVersionItem = SourceFileVersionData;

type FileVersionsState = Record<string, FileVersionItem[]>;
type FilesDataState = Record<string, FileVersionData>;
type FileDependenciesState = Record<string, FileDependencyItem[]>;
/** `/getResourcesUnderWhichFilesShared`'s body verbatim: the transmittal rows, with no status envelope. */
type SharedResourcesResponse = ApiPostResponse<"/fileOperations/getResourcesUnderWhichFilesShared">;
type FilesSharedUnderResourcesState = Record<string, SharedResourcesResponse>;

/** Merge payload for a new file version entry */
export interface FileVersionMergePayload {
  fileId: string;
  id?: string;
  awsFileName?: string;
  name?: string;
  fileVersionNumber?: number;
  createdDate: number;
  updatedDate?: number;
}

const fileVersionsSlice = createSlice({
  name: "fileVersions",
  initialState: {} as FileVersionsState,
  reducers: {
    setFileVersionData: (state, action: PayloadAction<{ fileId: string; data: FileVersionItem[] }>) => {
      const { fileId, data } = action.payload;
      state[fileId] = data as FileVersionItem[];
    },
    mergeFileVersionData: (state, action: PayloadAction<FileVersionMergePayload>) => {
      const { fileId, id, ...entry } = action.payload;
      // Every reader of `state.fileVersions[fileId]` addresses rows by `id` (`SourceFileVersionData.id`
      // is the required `files.versions[]._id`), so a payload without a valid version id cannot be
      // read back and is not merged rather than being asserted into the list.
      const versionId = objectIdString.safeParse(id);
      if (!versionId.success) return;
      const existing = (state[fileId] ?? []) as FileVersionItem[];
      state[fileId] = [...existing, { ...entry, id: versionId.data }];
    },
    setFileVersionStatus: (state) => state,
  },
});

const filesDataSlice = createSlice({
  name: "filesData",
  initialState: {} as FilesDataState,
  reducers: {
    setFilesData: (state, action: PayloadAction<Record<string, FileVersionData>>) => ({
      ...state,
      ...action.payload,
    }),
    resetFilesData: (state) => state,
    modifyFileUnitsData: (
      state,
      action: PayloadAction<{ fileVersionId: string; unitsNew: UpdateFileUnitsRequest["unitsNew"] }>,
    ) => {
      const { fileVersionId, unitsNew } = action.payload;
      const fileData = state[fileVersionId];
      if (!fileData) return state;
      const updatedUnits = { ...fileData.Units, ...unitsNew };
      return {
        ...state,
        [fileVersionId]: { ...fileData, Units: updatedUnits },
      };
    },
  },
});

const fileDependenciesSlice = createSlice({
  name: "fileDependencies",
  initialState: {} as FileDependenciesState,
  reducers: {
    setFileDependencies: (state, action: PayloadAction<{ fileVersionId: string; data: FileDependencyItem[] }>) => {
      const { fileVersionId, data } = action.payload;
      state[fileVersionId] = [...data];
    },
  },
});

const filesSharedUnderResourcesSlice = createSlice({
  name: "filesSharedUnderResources",
  initialState: {} as FilesSharedUnderResourcesState,
  reducers: {
    setResourcesUnderWhichFilesShared: (
      _state,
      action: PayloadAction<{ fileId: string; data: SharedResourcesResponse }>,
    ) => ({
      [action.payload.fileId]: action.payload.data,
    }),
  },
});

export interface DependencyQuerySettings {
  searchParameter: string;
  searchCondition: string;
  multiDependencyPreference: string;
}

type DependencyQuerySettingsState = Record<string, DependencyQuerySettings | null>;

const dependencyQuerySettingsSlice = createSlice({
  name: "dependencyQuerySettings",
  initialState: {} as DependencyQuerySettingsState,
  reducers: {
    setDependencyQuerySettings: (
      state,
      action: PayloadAction<{ fileVersionId: string; data: DependencyQuerySettings | null }>,
    ) => {
      state[action.payload.fileVersionId] = action.payload.data;
    },
  },
});

const currentFileVersionIdSlice = createSlice({
  name: "currentFileVersionId",
  initialState: "",
  reducers: {
    setFileVersionId: (_state, action: PayloadAction<string>) => action.payload,
  },
});

/**
 * Read one entry out of the `filesData` slice by an id that may be absent.
 *
 * The viewer's `fileVersionId` is `undefined` while no file is mounted, and `Record<string, T>`
 * cannot be indexed by `string | undefined`. A missing id simply has no entry, which is what
 * every caller already handles.
 */
export const selectFileVersionData = (
  filesData: FilesDataState,
  fileVersionId: string | undefined,
): FileVersionData | undefined => (fileVersionId === undefined ? undefined : filesData[fileVersionId]);

export const { setFileVersionData, mergeFileVersionData, setFileVersionStatus } = fileVersionsSlice.actions;

export const { setFilesData: setFilesDataAction, resetFilesData, modifyFileUnitsData } = filesDataSlice.actions;

export const { setFileDependencies: setFileDependenciesAction } = fileDependenciesSlice.actions;

export const { setDependencyQuerySettings: setDependencyQuerySettingsAction } = dependencyQuerySettingsSlice.actions;

export const { setResourcesUnderWhichFilesShared: setResourcesUnderWhichFilesSharedAction } =
  filesSharedUnderResourcesSlice.actions;

export const { setFileVersionId: setFileVersionIdAction } = currentFileVersionIdSlice.actions;

export const fileVersions = fileVersionsSlice.reducer;
export const setFilesData = filesDataSlice.reducer;
export const setFileDependencies = fileDependenciesSlice.reducer;
export const dependencyQuerySettings = dependencyQuerySettingsSlice.reducer;
export const filesSharedUnderResources = filesSharedUnderResourcesSlice.reducer;
export const currentFileVersionId = currentFileVersionIdSlice.reducer;
